import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  Activity,
  Bell,
  Building2,
  ChevronLeft,
  ChevronRight,
  Download,
  LineChart,
  Pause,
  Play,
  PlusCircle,
  RefreshCw,
  ShieldCheck,
  Timer,
  UserPlus,
  Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { EnterpriseSalesNav } from '../components/enterprise/EnterpriseSalesNav';
import { useSEO } from '../lib/seo';

/**
 * Public illustrative enterprise dashboard for live sales pitches.
 * Uses static sample numbers — not connected to tenant data.
 */

const demoSteps = [
  {
    id: 'positioning',
    title: 'Positioning opener',
    durationMinutes: 1,
    anchorId: 'demo-header',
    points: [
      'Set context: this is a governed enterprise relationship-intelligence workspace.',
      'Explain that all numbers are sample data to demonstrate product flow and buyer experience.',
    ],
  },
  {
    id: 'executive-signals',
    title: 'Executive signal panel',
    durationMinutes: 2,
    anchorId: 'demo-kpis',
    points: [
      'Walk through active members, check-ins, high-tension rate, and 72h forecast.',
      'Frame this as early warning visibility before attrition and productivity degradation.',
    ],
  },
  {
    id: 'cohorts-governance',
    title: 'Cohorts + tenant governance',
    durationMinutes: 2,
    anchorId: 'demo-cohorts-governance',
    points: [
      'Show cohort distribution and tenant policy snapshot in one screen.',
      'Reinforce procurement fit: seat governance, status controls, and domain assurance.',
    ],
  },
  {
    id: 'interventions',
    title: 'Intervention queue',
    durationMinutes: 2,
    anchorId: 'demo-intervention-queue',
    points: [
      'Highlight prioritized operational signals for managers and benefits teams.',
      'Describe this as the action layer that turns intelligence into intervention motion.',
    ],
  },
  {
    id: 'close',
    title: 'Close and CTA',
    durationMinutes: 1,
    anchorId: 'demo-cta',
    points: [
      'Close with launch path: pilot quickly, then scale with enterprise controls.',
      'Drive next action: book scoped pilot and define success metrics.',
    ],
  },
];

function formatDuration(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

type DemoSeat = {
  id: string;
  name: string;
  role: string;
  status: 'active' | 'pending';
};

type DemoCheckIn = {
  id: string;
  seatId: string;
  rtiScore: number;
  domain: 'leadership' | 'friendship' | 'romantic';
  occurredAt: string;
  note: string;
};

const initialDemoSeats: DemoSeat[] = [
  { id: 's1', name: 'Maya Patel', role: 'manager', status: 'active' },
  { id: 's2', name: 'Jordan Lee', role: 'member', status: 'active' },
  { id: 's3', name: 'Ari Thompson', role: 'member', status: 'active' },
  { id: 's4', name: 'Casey Rivera', role: 'member', status: 'pending' },
  { id: 's5', name: 'Sam Morgan', role: 'hr_partner', status: 'active' },
];

const initialDemoCheckIns: DemoCheckIn[] = [
  { id: 'c1', seatId: 's1', rtiScore: 64, domain: 'leadership', occurredAt: new Date(Date.now() - 2 * 86400000).toISOString(), note: 'Manager stress spike in team handoff.' },
  { id: 'c2', seatId: 's2', rtiScore: 41, domain: 'friendship', occurredAt: new Date(Date.now() - 4 * 86400000).toISOString(), note: 'Recovering after conflict conversation.' },
  { id: 'c3', seatId: 's3', rtiScore: 73, domain: 'romantic', occurredAt: new Date(Date.now() - 8 * 86400000).toISOString(), note: 'High strain and sleep disruption pattern.' },
  { id: 'c4', seatId: 's5', rtiScore: 35, domain: 'leadership', occurredAt: new Date(Date.now() - 11 * 86400000).toISOString(), note: 'Stable support utilization week.' },
  { id: 'c5', seatId: 's2', rtiScore: 58, domain: 'friendship', occurredAt: new Date(Date.now() - 17 * 86400000).toISOString(), note: 'Elastic movement after workload shift.' },
  { id: 'c6', seatId: 's1', rtiScore: 49, domain: 'leadership', occurredAt: new Date(Date.now() - 21 * 86400000).toISOString(), note: 'Moderate tension during re-org planning.' },
];

export default function EnterpriseSalesDemo() {
  useSEO({
    title: 'Enterprise Demo (Pitch) | TENSION',
    description:
      'Illustrative TENSION Enterprise dashboard for sales conversations — sample metrics only.',
    canonical: '/enterprise/sales-demo',
    ogType: 'website',
  });

  const [stepIndex, setStepIndex] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [tenantStatus, setTenantStatus] = useState('pilot_active');
  const [tenantSeatLimit, setTenantSeatLimit] = useState(25);
  const [domainVerified, setDomainVerified] = useState(true);
  const [allowedDomains, setAllowedDomains] = useState('acmebenefits.com, tensionlabs.net');
  const [seats, setSeats] = useState<DemoSeat[]>(initialDemoSeats);
  const [checkIns, setCheckIns] = useState<DemoCheckIn[]>(initialDemoCheckIns);
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [newCheckInSeatId, setNewCheckInSeatId] = useState('s1');
  const [newCheckInScore, setNewCheckInScore] = useState(52);
  const [newCheckInDomain, setNewCheckInDomain] = useState<'leadership' | 'friendship' | 'romantic'>('leadership');
  const [newCheckInNote, setNewCheckInNote] = useState('Demo check-in added live during walkthrough.');

  useEffect(() => {
    if (!timerRunning) return;
    const id = window.setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => window.clearInterval(id);
  }, [timerRunning]);

  const activeStep = demoSteps[stepIndex];
  const totalRecommendedMinutes = useMemo(
    () => demoSteps.reduce((sum, step) => sum + step.durationMinutes, 0),
    [],
  );

  const goToStep = (nextIndex: number) => {
    const bounded = Math.max(0, Math.min(demoSteps.length - 1, nextIndex));
    setStepIndex(bounded);
    const targetId = demoSteps[bounded]?.anchorId;
    if (!targetId) return;
    const node = document.getElementById(targetId);
    if (node) {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const activeMembers = useMemo(() => seats.filter((seat) => seat.status === 'active').length, [seats]);
  const pendingSeats = useMemo(() => seats.filter((seat) => seat.status === 'pending').length, [seats]);

  const checkInsLast30 = useMemo(
    () => checkIns.filter((entry) => Date.now() - new Date(entry.occurredAt).getTime() <= 30 * 86400000),
    [checkIns],
  );
  const avgRti = useMemo(() => {
    if (checkInsLast30.length === 0) return 0;
    return Math.round(checkInsLast30.reduce((sum, row) => sum + row.rtiScore, 0) / checkInsLast30.length);
  }, [checkInsLast30]);
  const highTensionCount = useMemo(() => checkInsLast30.filter((row) => row.rtiScore >= 61).length, [checkInsLast30]);
  const highTensionRate = useMemo(
    () => (checkInsLast30.length === 0 ? 0 : Math.round((highTensionCount / checkInsLast30.length) * 100)),
    [checkInsLast30.length, highTensionCount],
  );
  const cohortPct = useMemo(() => {
    const total = checkInsLast30.length || 1;
    const stable = checkInsLast30.filter((row) => row.rtiScore <= 35).length;
    const high = checkInsLast30.filter((row) => row.rtiScore >= 61).length;
    const elastic = total - stable - high;
    return {
      stable: Math.round((stable / total) * 100),
      elastic: Math.round((elastic / total) * 100),
      high: Math.round((high / total) * 100),
    };
  }, [checkInsLast30]);

  const trendDelta14 = useMemo(() => {
    const now = Date.now();
    const recent = checkIns.filter((row) => now - new Date(row.occurredAt).getTime() <= 14 * 86400000);
    const prior = checkIns.filter((row) => {
      const age = now - new Date(row.occurredAt).getTime();
      return age > 14 * 86400000 && age <= 28 * 86400000;
    });
    if (recent.length === 0 || prior.length === 0) return 0;
    const recentAvg = recent.reduce((sum, row) => sum + row.rtiScore, 0) / recent.length;
    const priorAvg = prior.reduce((sum, row) => sum + row.rtiScore, 0) / prior.length;
    return Math.round(recentAvg - priorAvg);
  }, [checkIns]);

  const forecastRisk = useMemo(() => {
    if (highTensionRate >= 35 || trendDelta14 >= 8) return 'High';
    if (highTensionRate >= 20 || trendDelta14 >= 3) return 'Medium';
    return 'Low';
  }, [highTensionRate, trendDelta14]);

  const interventionQueue = useMemo(() => {
    const queue: string[] = [];
    if (highTensionRate >= 25) queue.push(`High-tension check-ins are ${highTensionRate}% (target < 20%).`);
    if (pendingSeats > 0) queue.push(`${pendingSeats} pending seat invite(s) require onboarding follow-up.`);
    if (trendDelta14 > 0) queue.push(`RTI 14-day trend increased by +${trendDelta14} vs prior period.`);
    if (queue.length === 0) queue.push('No critical flags — maintain weekly leadership digest cadence.');
    return queue;
  }, [highTensionRate, pendingSeats, trendDelta14]);

  const seatUtilization = tenantSeatLimit > 0 ? Math.round((activeMembers / tenantSeatLimit) * 100) : 0;

  const addInviteSeat = () => {
    const name = inviteName.trim();
    if (!name) return;
    const next: DemoSeat = {
      id: `s${Date.now()}`,
      name,
      role: inviteRole.trim() || 'member',
      status: 'pending',
    };
    setSeats((prev) => [next, ...prev]);
    setInviteName('');
  };

  const toggleSeatStatus = (id: string) => {
    setSeats((prev) =>
      prev.map((seat) =>
        seat.id === id
          ? { ...seat, status: seat.status === 'active' ? 'pending' : 'active' }
          : seat,
      ),
    );
  };

  const addDemoCheckIn = () => {
    if (!newCheckInSeatId) return;
    const entry: DemoCheckIn = {
      id: `c${Date.now()}`,
      seatId: newCheckInSeatId,
      rtiScore: Math.max(0, Math.min(100, Number(newCheckInScore) || 0)),
      domain: newCheckInDomain,
      occurredAt: new Date().toISOString(),
      note: newCheckInNote.trim() || 'Live demo check-in entry.',
    };
    setCheckIns((prev) => [entry, ...prev]);
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,rgba(30,64,175,0.2),transparent_35%),linear-gradient(180deg,#020617_0%,#0f172a_50%,#020617_100%)]">
      <EnterpriseSalesNav variant="dark" />
      <div className="mx-auto max-w-6xl space-y-6 px-4 py-8">
        <div className="rounded-xl border border-amber-400/40 bg-amber-950/40 px-4 py-3 text-sm text-amber-100">
          <strong className="font-semibold">Interactive sandbox.</strong> This page runs a fully hands-on frontend demo: live controls,
          seat changes, and check-in events update metrics in real time (without touching production tenant data).
        </div>

        <header id="demo-header" className="overflow-hidden rounded-2xl border border-slate-700 bg-slate-950/90 p-6 shadow-xl md:p-8">
          <img
            src="/branding/tension-enterprise-logo.svg"
            alt="TENSION Enterprise"
            className="mb-4 h-auto max-w-md rounded-xl border border-slate-700/80 p-1"
          />
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Illustrative command view</p>
          <h1 className="mt-2 text-2xl font-bold text-white md:text-3xl">Acme Benefits — pilot workspace</h1>
          <p className="mt-2 max-w-2xl text-slate-300">
            Explore how executives see cohort movement, seat health, and operational signals in a governed deployment.
          </p>
        </header>

        <section id="demo-kpis" className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[
            { label: 'Active members', value: String(activeMembers), icon: Users },
            { label: 'Check-ins (30d)', value: String(checkInsLast30.length), icon: Activity },
            { label: 'High-tension rate', value: `${highTensionRate}%`, icon: LineChart },
            { label: 'Forecast risk (72h)', value: forecastRisk, icon: Bell },
          ].map(({ label, value, icon: Icon }) => (
            <Card key={label} className="border-slate-700 bg-slate-900/90 p-4 text-slate-100">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
                <Icon className="h-5 w-5 text-blue-300" aria-hidden />
              </div>
              <p className="mt-2 text-2xl font-bold text-white">{value}</p>
            </Card>
          ))}
        </section>

        <section id="demo-cohorts-governance" className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
          <Card className="space-y-4 border-slate-700 bg-slate-950/90 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Cohort mix (live from sandbox check-ins)</h2>
              <Button size="sm" variant="outline" className="enterprise-admin-button" type="button">
                <Download className="mr-2 h-4 w-4" aria-hidden />
                Export demo CSV
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                ['Stable', `${cohortPct.stable}%`, 'emerald'],
                ['Elastic', `${cohortPct.elastic}%`, 'amber'],
                ['High tension', `${cohortPct.high}%`, 'rose'],
              ].map(([k, v, tone]) => (
                <div
                  key={k}
                  className={`rounded-xl border border-slate-700 bg-slate-900/80 p-4 ${
                    tone === 'emerald'
                      ? 'text-emerald-200'
                      : tone === 'amber'
                        ? 'text-amber-200'
                        : 'text-rose-200'
                  }`}
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{k}</p>
                  <p className="mt-1 text-2xl font-bold">{v}</p>
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-slate-700/60 bg-slate-900/70 p-3 text-xs text-slate-300">
              <p>Average RTI (30d): <span className="font-semibold text-slate-100">{avgRti}</span></p>
              <p className="mt-1">14-day trend delta: <span className="font-semibold text-slate-100">{trendDelta14 >= 0 ? `+${trendDelta14}` : trendDelta14}</span></p>
            </div>
          </Card>

          <Card className="space-y-4 border-slate-700 bg-slate-950/90 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Tenant controls (interactive)</h2>
              <Building2 className="h-6 w-6 text-blue-300" aria-hidden />
            </div>
            <div className="grid gap-2 text-sm text-slate-300">
              <label className="space-y-1">
                <span className="text-xs text-slate-400">Status</span>
                <select
                  value={tenantStatus}
                  onChange={(e) => setTenantStatus(e.target.value)}
                  className="w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-slate-100"
                >
                  <option value="pilot_active">pilot_active</option>
                  <option value="pilot_watch">pilot_watch</option>
                  <option value="expansion_ready">expansion_ready</option>
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-xs text-slate-400">Seat limit</span>
                <input
                  type="number"
                  min={1}
                  value={tenantSeatLimit}
                  onChange={(e) => setTenantSeatLimit(Math.max(1, Number(e.target.value) || 1))}
                  className="w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-slate-100"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs text-slate-400">Allowed domains</span>
                <input
                  value={allowedDomains}
                  onChange={(e) => setAllowedDomains(e.target.value)}
                  className="w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-slate-100"
                />
              </label>
              <label className="mt-1 inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={domainVerified}
                  onChange={(e) => setDomainVerified(e.target.checked)}
                />
                Domain verified
              </label>
            </div>
            <ul className="space-y-2 border-t border-slate-800 pt-2 text-sm text-slate-300">
              <li className="flex justify-between">
                <span>Seat utilization</span>
                <span className="font-medium text-slate-100">{seatUtilization}%</span>
              </li>
              <li className="flex justify-between">
                <span>Pending invites</span>
                <span className="font-medium text-slate-100">{pendingSeats}</span>
              </li>
              <li className="flex justify-between">
                <span>Domain status</span>
                <span className={domainVerified ? 'text-emerald-300' : 'text-amber-300'}>{domainVerified ? 'Verified' : 'Pending'}</span>
              </li>
            </ul>
            <Button variant="outline" className="enterprise-admin-button w-full" type="button">
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
              Apply control changes
            </Button>
          </Card>
        </section>

        <Card id="demo-intervention-queue" className="border-slate-700 bg-slate-950/90 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-white">Intervention queue (live)</h2>
              <p className="mt-1 text-sm text-slate-400">
                Priority signals generated from your sandbox controls and check-ins.
              </p>
            </div>
            <ShieldCheck className="h-8 w-8 shrink-0 text-cyan-300" aria-hidden />
          </div>
          <ul className="mt-4 space-y-2">
            {interventionQueue.map((line) => (
              <li
                key={line}
                className="rounded-lg border border-slate-700/80 bg-slate-900/70 px-4 py-3 text-sm text-slate-200"
              >
                {line}
              </li>
            ))}
          </ul>
        </Card>

        <section className="grid gap-6 lg:grid-cols-2">
          <Card className="space-y-4 border-slate-700 bg-slate-950/90 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Seat controls</h2>
              <UserPlus className="h-5 w-5 text-blue-300" aria-hidden />
            </div>
            <div className="flex gap-2">
              <input
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Invite name"
                className="flex-1 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100"
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100"
              >
                <option value="member">member</option>
                <option value="manager">manager</option>
                <option value="hr_partner">hr_partner</option>
              </select>
              <Button type="button" size="sm" className="bg-blue-600 text-white" onClick={addInviteSeat}>
                <PlusCircle className="mr-1 h-3.5 w-3.5" aria-hidden />
                Add
              </Button>
            </div>
            <ul className="space-y-2 text-sm">
              {seats.map((seat) => (
                <li key={seat.id} className="rounded-lg border border-slate-700/80 bg-slate-900/70 px-3 py-2 text-slate-200">
                  <div className="flex items-center justify-between gap-2">
                    <span>{seat.name} · {seat.role}</span>
                    <button
                      type="button"
                      onClick={() => toggleSeatStatus(seat.id)}
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        seat.status === 'active'
                          ? 'border border-emerald-400/40 bg-emerald-500/15 text-emerald-200'
                          : 'border border-amber-400/40 bg-amber-500/15 text-amber-200'
                      }`}
                    >
                      {seat.status}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="space-y-4 border-slate-700 bg-slate-950/90 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Check-ins (live)</h2>
              <Activity className="h-5 w-5 text-blue-300" aria-hidden />
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <select
                value={newCheckInSeatId}
                onChange={(e) => setNewCheckInSeatId(e.target.value)}
                className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100"
              >
                {seats.map((seat) => (
                  <option key={seat.id} value={seat.id}>{seat.name}</option>
                ))}
              </select>
              <input
                type="number"
                min={0}
                max={100}
                value={newCheckInScore}
                onChange={(e) => setNewCheckInScore(Number(e.target.value))}
                className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100"
              />
              <select
                value={newCheckInDomain}
                onChange={(e) => setNewCheckInDomain(e.target.value as 'leadership' | 'friendship' | 'romantic')}
                className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100"
              >
                <option value="leadership">leadership</option>
                <option value="friendship">friendship</option>
                <option value="romantic">romantic</option>
              </select>
              <Button type="button" size="sm" className="bg-blue-600 text-white" onClick={addDemoCheckIn}>
                <PlusCircle className="mr-1 h-3.5 w-3.5" aria-hidden />
                Add check-in
              </Button>
            </div>
            <textarea
              value={newCheckInNote}
              onChange={(e) => setNewCheckInNote(e.target.value)}
              className="h-16 w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100"
            />
            <ul className="max-h-56 space-y-2 overflow-y-auto text-sm">
              {checkIns.slice(0, 10).map((entry) => {
                const seat = seats.find((row) => row.id === entry.seatId);
                return (
                  <li key={entry.id} className="rounded-lg border border-slate-700/80 bg-slate-900/70 px-3 py-2 text-slate-200">
                    <p className="font-medium text-slate-100">
                      {seat?.name || 'Unknown'} · RTI {entry.rtiScore} · {entry.domain}
                    </p>
                    <p className="text-xs text-slate-400">{new Date(entry.occurredAt).toLocaleString()}</p>
                    <p className="mt-1 text-xs text-slate-300">{entry.note}</p>
                  </li>
                );
              })}
            </ul>
          </Card>
        </section>

        <div id="demo-cta" className="flex flex-wrap justify-center gap-3 pb-12">
          <Button variant="outline" className="border-slate-600 bg-slate-900 text-slate-100" asChild>
            <Link to="/enterprise">Public enterprise overview</Link>
          </Button>
          <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white" asChild>
            <Link to="/business-inquiries?inquiryType=enterprise">Request a real pilot</Link>
          </Button>
        </div>
      </div>

      <div className="fixed bottom-4 right-4 z-50 w-[min(94vw,390px)]">
        <Card className="border-slate-600 bg-slate-950/95 p-4 text-slate-100 shadow-2xl shadow-black/40 backdrop-blur">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Demo mode</p>
              <h2 className="text-sm font-semibold text-white">{activeStep.title}</h2>
              <p className="text-xs text-slate-400">
                Step {stepIndex + 1} of {demoSteps.length} · Target {activeStep.durationMinutes} min
              </p>
            </div>
            <div className="text-right">
              <p className="inline-flex items-center gap-1 text-xs text-slate-300">
                <Timer className="h-3.5 w-3.5" aria-hidden />
                Elapsed
              </p>
              <p className="font-mono text-lg font-semibold text-cyan-200">{formatDuration(elapsedSeconds)}</p>
            </div>
          </div>

          <ul className="mt-3 space-y-1.5 text-xs leading-relaxed text-slate-200">
            {activeStep.points.map((point) => (
              <li key={point} className="rounded-md border border-slate-700/70 bg-slate-900/70 px-2.5 py-1.5">
                {point}
              </li>
            ))}
          </ul>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="enterprise-admin-button"
              onClick={() => goToStep(stepIndex - 1)}
              disabled={stepIndex === 0}
            >
              <ChevronLeft className="mr-1 h-3.5 w-3.5" aria-hidden />
              Prev
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="enterprise-admin-button"
              onClick={() => goToStep(stepIndex + 1)}
              disabled={stepIndex >= demoSteps.length - 1}
            >
              Next
              <ChevronRight className="ml-1 h-3.5 w-3.5" aria-hidden />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="enterprise-admin-button"
              onClick={() => setTimerRunning((prev) => !prev)}
            >
              {timerRunning ? <Pause className="mr-1 h-3.5 w-3.5" aria-hidden /> : <Play className="mr-1 h-3.5 w-3.5" aria-hidden />}
              {timerRunning ? 'Pause' : 'Start'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="enterprise-admin-button"
              onClick={() => {
                setElapsedSeconds(0);
                setTimerRunning(false);
                goToStep(0);
              }}
            >
              Reset
            </Button>
          </div>

          <p className="mt-2 text-[11px] text-slate-400">
            Recommended full demo length: {totalRecommendedMinutes} minutes.
          </p>
        </Card>
      </div>
    </div>
  );
}
